import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Pill, Syringe, TestTubes, Lock, ArrowRight, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { track } from "../lib/analytics";

const STORES = [
  {
    key: "supplements",
    icon: Pill,
    title: "Supplements",
    blurb:
      "Clinical-grade supplements from cGMP-certified, FDA-registered facilities: the same manufacturing standard used in clinical settings.",
    items: [],
    gated: false,
    cta: "Shop Supplements",
    href: "https://supplements.sassyfitness.com",
  },
  {
    key: "peptides",
    icon: Syringe,
    title: "Peptides",
    blurb:
      "Provider-sourced peptides from FDA-registered U.S. compounding pharmacies, under independent licensed clinical oversight.",
    items: [],
    gated: true,
    cta: "Member Access",
    href: "/services/rx-club",
    internal: true,
  },
  {
    key: "labs",
    icon: TestTubes,
    title: "Hormones",
    blurb:
      "Comprehensive testing ordered and interpreted by licensed providers, then acted on with your coach.",
    items: ["+Labs", "+BHRT", "+Thyroid", "+GI-MAP"],
    gated: true,
    cta: "Member Access",
    href: "/services/rx-club",
    internal: true,
  },
];

const StoresPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Stores | Sassy Full Stack Fitness";
  }, []);

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="stores-page">
      <Header />
      <main className="flex-1 pt-28 md:pt-32 pb-24 px-[5%]">
        <div className="max-w-[1200px] mx-auto">
          {/* Header */}
          <div className="text-center">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/50 hover:text-white/80 transition-colors mb-6 focus-ring rounded-sm"
              data-testid="stores-back"
            >
              <ArrowLeft size={14} /> Sassy Full Stack Fitness
            </button>
            <h1
              className="font-display font-black uppercase leading-[1.02] text-[40px] md:text-[68px] text-white"
              style={{ letterSpacing: "0.01em" }}
              data-testid="stores-title"
            >
              <span className="text-gold-gradient">Stores</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/65 font-medium max-w-2xl mx-auto leading-relaxed">
              Everything we put in our clients' hands, in one place. Supplements are open to
              everyone. Peptides and labs are handled through our independent providers, for
              <span className="text-primary font-semibold"> Rx Club members</span>.
            </p>
          </div>

          {/* 3 boxes — horizontal on desktop, stacked on mobile */}
          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {STORES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`store-card-${s.key}`}
                  className={`group relative flex flex-col overflow-hidden rounded-3xl border p-7 md:p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                    s.gated
                      ? "border-primary/25 bg-gradient-to-b from-primary/[0.07] to-white/[0.02] hover:border-primary/50"
                      : "border-white/10 bg-white/[0.03] hover:border-white/25"
                  }`}
                  style={{ animation: `fadeUp 0.5s ease ${0.08 * i}s both` }}
                >
                  {/* top gold hairline that grows on hover */}
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-40 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* gated badge */}
                  {s.gated && (
                    <span
                      className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/[0.12] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary"
                      data-testid={`store-badge-${s.key}`}
                    >
                      <Lock size={11} strokeWidth={2.5} /> Rx Club Members Only
                    </span>
                  )}

                  {/* icon */}
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-105 ${
                      s.gated ? "bg-primary/15 text-primary" : "bg-white/[0.06] text-white"
                    }`}
                  >
                    <Icon size={26} strokeWidth={2} />
                  </span>

                  {/* title */}
                  <h2 className="mt-6 font-display font-black uppercase tracking-tight text-3xl md:text-4xl text-white">
                    {s.title}
                  </h2>

                  {/* sub-items (labs) */}
                  {s.items.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid={`store-items-${s.key}`}>
                      {s.items.map((it) => (
                        <span
                          key={it}
                          className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-white/75"
                        >
                          {it}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* blurb */}
                  <p className="mt-4 text-sm text-white/60 leading-relaxed">{s.blurb}</p>

                  {/* CTA pinned to bottom */}
                  <div className="mt-auto pt-7">
                    <a
                      href={s.href}
                      onClick={
                        s.href === "#"
                          ? (e) => e.preventDefault()
                          : s.internal
                          ? (e) => {
                              e.preventDefault();
                              track("store_cta_click", { store: s.key, destination: s.href });
                              navigate(s.href);
                            }
                          : () => track("outbound_click", { url: s.href, label: s.title, store: s.key })
                      }
                      target={!s.internal && s.href !== "#" ? "_blank" : undefined}
                      rel={!s.internal && s.href !== "#" ? "noopener noreferrer" : undefined}
                      data-testid={`store-cta-${s.key}`}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full font-black uppercase tracking-wide text-[13px] px-6 py-3.5 transition focus-ring ${
                        s.gated
                          ? "border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground"
                          : "bg-primary text-primary-foreground hover:brightness-110 shadow-[0_10px_36px_-10px_rgba(212,175,55,0.6)]"
                      }`}
                    >
                      {s.cta}
                      <ArrowRight size={16} strokeWidth={2.5} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Not a member CTA */}
          <div className="mt-12 md:mt-14 text-center">
            <p className="text-sm text-white/55">
              Peptides and labs are an Rx Club benefit.{" "}
              <button
                onClick={() => {
                  track("store_cta_click", { store: "rx_club_learn" });
                  navigate("/services/rx-club");
                }}
                className="font-bold text-primary underline underline-offset-4 decoration-2 decoration-primary/60 hover:decoration-primary transition-colors focus-ring rounded-sm"
                data-testid="stores-join-rxclub"
              >
                Learn about the Rx Club
              </button>
            </p>
          </div>

          {/* Legal disclaimer — mirrors Rx Club positioning */}
          <p className="mt-14 text-[11px] text-white/35 leading-relaxed max-w-3xl mx-auto text-center" data-testid="stores-disclaimer">
            Sassy &ldquo;Full Stack&rdquo; Fitness is a coaching and wellness brand, not a medical
            provider, and does not sell, prescribe, or bill for any medication, hormone therapy,
            peptide, or lab service. Peptides and lab services are provided independently by
            third-party licensed medical providers. Sassy &ldquo;Full Stack&rdquo; Fitness receives
            no commission, fee, or other compensation for any introduction, referral, prescription,
            or purchase made through our provider partners. Individual results may vary.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StoresPage;
